import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { siteInfo } from '../data/mockData';

const FuneralValor = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // Atualizar título e meta description
  useEffect(() => {
    document.title = 'Valor de Funeral RJ | Quanto Custa um Funeral? | 24 Horas';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Consulte o valor de funeral no RJ. Informações sobre funeral simples, enterro, sepultamento e cremação. Atendimento funerário 24 horas.';
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqItems = [
    {
      question: 'Quanto custa um funeral no RJ?',
      answer: 'O valor depende dos serviços contratados, como urna funerária, remoção, preparação, transporte, velório e destino final. Entre em contato para receber um orçamento de acordo com a necessidade da família.'
    },
    {
      question: 'Quanto custa um funeral simples?',
      answer: 'O preço de um funeral simples depende principalmente da urna, remoção, preparação, transporte e demais serviços necessários. A equipe pode apresentar as opções disponíveis e informar os valores.'
    },
    {
      question: 'Qual o valor de um enterro?',
      answer: 'O custo pode envolver tanto os serviços funerários quanto despesas relacionadas ao cemitério. O valor varia conforme o local e as condições do sepultamento.'
    },
    {
      question: 'Qual o valor de um sepultamento?',
      answer: 'Os valores podem variar conforme o cemitério, jazigo, taxas e serviços necessários. Consulte nossa equipe para obter orientação.'
    },
    {
      question: 'A funerária atende 24 horas?',
      answer: 'Sim. O atendimento funerário funciona 24 horas para orientar famílias e receber solicitações de serviços.'
    },
    {
      question: 'Posso solicitar orçamento pelo WhatsApp?',
      answer: 'Sim. Entre em contato pelo WhatsApp e informe a situação para que nossa equipe possa orientar sobre os serviços e valores.'
    }
  ];

  const servicosVariacao = [
    'Modelo da urna funerária',
    'Local da remoção',
    'Preparação e conservação',
    'Transporte funerário',
    'Velório',
    'Cemitério escolhido',
    'Sepultamento',
    'Cremação',
    'Serviços adicionais solicitados pela família'
  ];

  const servicosFuneralSimples = [
    'Atendimento funerário',
    'Remoção',
    'Urna funerária',
    'Preparação do falecido',
    'Transporte funerário',
    'Orientação sobre documentação',
    'Organização do funeral',
    'Encaminhamento para sepultamento ou cremação'
  ];

  // Schema.org
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Serviços Funerários",
    "name": "Funeral no Rio de Janeiro",
    "description": "Atendimento funerário 24 horas com orientação para a família, remoção, preparação, velório, sepultamento e cremação.",
    "provider": {
      "@type": "FuneralHome",
      "name": "Santa Casa Assistencial",
      "telephone": "+55-21-4106-4022",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Rio de Janeiro",
        "addressRegion": "RJ",
        "addressCountry": "BR"
      },
      "openingHours": "Mo-Su 00:00-23:59"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-teal-500 to-teal-700 text-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Quanto Custa um Funeral no RJ?
            </h1>
            <h2 className="text-2xl md:text-3xl mb-6 text-teal-50 font-light">
              Funeral no Rio de Janeiro com Atendimento 24 Horas
            </h2>
            <p className="text-lg md:text-xl mb-4 max-w-4xl mx-auto leading-relaxed">
              <strong>Precisa saber o valor de um funeral no Rio de Janeiro?</strong>
            </p>
            <p className="text-base md:text-lg mb-8 max-w-4xl mx-auto leading-relaxed text-teal-50">
              A Santa Casa Assistencial oferece <strong>atendimento funerário 24 horas</strong>, com orientação para a família, remoção, preparação, urna funerária, velório, sepultamento e cremação.
            </p>
            <p className="text-xl font-semibold mb-8">
              Fale agora com nossa equipe e solicite um orçamento.
            </p>

            {/* CTAs Principais */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/552141064022?text=Gostaria%20de%20consultar%20o%20valor%20de%20funeral"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: 'whatsapp_click',
                    click_location: 'hero_funeral_valor',
                    intent: 'consultar_valor'
                  });
                }}
                className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                CONSULTAR VALOR PELO WHATSAPP
              </a>
              <a
                href="tel:41064022"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: 'phone_click',
                    click_location: 'hero_funeral_valor'
                  });
                }}
                className="inline-flex items-center justify-center bg-white text-teal-700 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <Phone className="w-5 h-5 mr-2" />
                LIGAR AGORA
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Valor de Funeral no RJ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Valor de Funeral no Rio de Janeiro
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            O valor de um funeral varia conforme os serviços necessários e as escolhas da família.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            <strong>O orçamento pode variar de acordo com:</strong>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {servicosVariacao.map((servico, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white rounded-lg p-4 shadow-sm">
                <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-1" />
                <span className="text-gray-800">{servico}</span>
              </div>
            ))}
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Nossa equipe explica os serviços e apresenta as opções disponíveis antes da contratação.
          </p>
          <p className="text-xl font-bold text-gray-900 mb-6 text-center">
            Precisa saber o valor agora?
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center">
            Entre em contato e informe onde ocorreu o falecimento e onde será realizado o funeral.
          </p>

          <div className="text-center">
            <a
              href="https://wa.me/552141064022?text=Preciso%20de%20orçamento%20para%20funeral"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'valor_funeral_section',
                  urgency: 'high'
                });
              }}
              className="inline-block bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              SOLICITAR ORÇAMENTO 24 HORAS
            </a>
          </div>
        </div>
      </section>

      {/* Funeral Simples */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Quanto Custa um Funeral Simples?
          </h2>

          {/* Box de Preço Destacado */}
          <div className="bg-gradient-to-r from-teal-50 to-green-50 border-4 border-teal-500 rounded-2xl p-8 mb-8 text-center max-w-2xl mx-auto shadow-2xl">
            <p className="text-lg text-gray-600 mb-3">Funeral Simples</p>
            <p className="text-5xl md:text-6xl font-bold text-teal-600 mb-2">
              R$ 1.480<span className="text-2xl text-gray-500">*</span>
            </p>
            <p className="text-sm text-gray-600 mb-6">*Consulte condições e disponibilidade</p>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            O funeral simples é indicado para famílias que procuram os serviços necessários para a realização do funeral, sem adicionar itens que não sejam necessários.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            <strong>Dependendo da situação, o serviço poderá incluir:</strong>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {servicosFuneralSimples.map((servico, index) => (
              <div key={index} className="flex items-start space-x-3 bg-teal-50 rounded-lg p-4">
                <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-1" />
                <span className="text-gray-800">{servico}</span>
              </div>
            ))}
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            O valor depende das necessidades de cada atendimento.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center">
            Consulte nossa equipe para receber um orçamento detalhado.
          </p>

          <div className="text-center">
            <a
              href="https://wa.me/552141064022?text=Gostaria%20de%20saber%20o%20valor%20de%20funeral%20simples"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'funeral_simples_section',
                  package_type: 'simples',
                  package_value: '1480'
                });
              }}
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              CONSULTAR VALOR DO FUNERAL
            </a>
          </div>
        </div>
      </section>

      {/* Funeral Completo */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Quanto Custa um Funeral Completo?
          </h2>

          {/* Box de Preço Destacado */}
          <div className="bg-gradient-to-r from-teal-50 to-green-50 border-4 border-teal-500 rounded-2xl p-8 mb-8 text-center max-w-2xl mx-auto shadow-2xl relative">
            <div className="absolute -top-4 right-8">
              <span className="bg-red-500 text-white px-4 py-1 rounded-full text-sm font-bold uppercase shadow-lg">
                Mais Contratado
              </span>
            </div>
            <p className="text-lg text-gray-600 mb-3">Funeral Completo</p>
            <p className="text-5xl md:text-6xl font-bold text-teal-600 mb-2">
              R$ 2.890<span className="text-2xl text-gray-500">*</span>
            </p>
            <p className="text-sm text-gray-600 mb-6">*A partir de. Consulte condições completas</p>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            O valor de um funeral completo depende da composição dos serviços escolhidos pela família.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Um funeral poderá incluir urna funerária, remoção, preparação e conservação, ornamentação, transporte, velório, documentação e encaminhamento para sepultamento ou cremação.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center">
            Antes da contratação, nossa equipe poderá informar as opções disponíveis e seus respectivos valores.
          </p>

          <div className="text-center">
            <a
              href="https://wa.me/552141064022?text=Preciso%20de%20orçamento%20de%20funeral%20completo"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'funeral_completo_section',
                  package_type: 'completo',
                  package_value: '2890'
                });
              }}
              className="inline-block bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              PEDIR ORÇAMENTO PELO WHATSAPP
            </a>
          </div>
        </div>
      </section>

      {/* Cremação - NOVO */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Quanto Custa uma Cremação no RJ?
          </h2>

          {/* Box de Preço Destacado */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-4 border-orange-400 rounded-2xl p-8 mb-8 text-center max-w-2xl mx-auto shadow-2xl">
            <p className="text-lg text-gray-600 mb-3">Cremação Completa</p>
            <p className="text-5xl md:text-6xl font-bold text-orange-600 mb-2">
              R$ 2.490<span className="text-2xl text-gray-500">*</span>
            </p>
            <p className="text-sm text-gray-600 mb-6">*A partir de. Valor sujeito às condições e disponibilidade</p>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            O valor da cremação no Rio de Janeiro pode variar conforme os serviços contratados e o crematório escolhido.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            O serviço de cremação poderá incluir: remoção, preparação, urna funerária, documentação necessária, taxa de cremação, transporte ao crematório e entrega da urna com as cinzas.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center">
            Consulte nossa equipe para receber um orçamento detalhado de cremação.
          </p>

          <div className="text-center">
            <a
              href="https://wa.me/552141064022?text=Gostaria%20de%20saber%20o%20valor%20de%20cremação"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'cremacao_valor_section',
                  package_type: 'cremacao',
                  package_value: '2490'
                });
              }}
              className="inline-block bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              CONSULTAR VALOR DE CREMAÇÃO
            </a>
          </div>
        </div>
      </section>

      {/* Valor de Enterro */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Valor de Enterro no RJ
          </h2>

          {/* Box de Preço Destacado */}
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-4 border-blue-400 rounded-2xl p-8 mb-8 text-center max-w-2xl mx-auto shadow-2xl">
            <p className="text-lg text-gray-600 mb-3">Enterro Completo</p>
            <p className="text-5xl md:text-6xl font-bold text-blue-600 mb-2">
              R$ 1.780<span className="text-2xl text-gray-500">*</span>
            </p>
            <p className="text-sm text-gray-600 mb-6">*A partir de. Valor varia conforme cemitério e serviços</p>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Para saber quanto custa um enterro no Rio de Janeiro, é importante considerar separadamente os serviços da funerária e eventuais despesas cobradas pelo cemitério.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            O valor do enterro pode variar conforme o cemitério escolhido, existência ou não de jazigo, taxas e demais serviços necessários.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Nossa equipe orienta a família sobre os procedimentos para realização do funeral e sepultamento.
          </p>

          <div className="text-center">
            <a
              href="https://wa.me/552141064022?text=Gostaria%20de%20saber%20o%20valor%20de%20enterro"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'enterro_section',
                  package_type: 'enterro',
                  package_value: '1780'
                });
              }}
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              CONSULTAR VALOR DE ENTERRO
            </a>
          </div>
        </div>
      </section>

      {/* Valor de Sepultamento */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Valor de Sepultamento no Rio de Janeiro
          </h2>

          {/* Box de Preço Destacado */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-4 border-purple-400 rounded-2xl p-8 mb-8 text-center max-w-2xl mx-auto shadow-2xl">
            <p className="text-lg text-gray-600 mb-3">Sepultamento Tradicional</p>
            <p className="text-5xl md:text-6xl font-bold text-purple-600 mb-2">
              R$ 1.690<span className="text-2xl text-gray-500">*</span>
            </p>
            <p className="text-sm text-gray-600 mb-6">*A partir de. Consulte taxas do cemitério escolhido</p>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            O valor de um sepultamento pode variar conforme o cemitério, taxas aplicáveis, disponibilidade de jazigo e serviços contratados.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Se você precisa organizar um sepultamento no Rio de Janeiro, entre em contato para receber orientação sobre os procedimentos e custos envolvidos.
          </p>
          <p className="text-lg font-bold text-gray-900 mb-8 text-center">
            Atendimento 24 horas.
          </p>

          <div className="text-center">
            <a
              href="https://wa.me/552141064022?text=Preciso%20de%20informações%20sobre%20sepultamento"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'sepultamento_section',
                  package_type: 'sepultamento',
                  package_value: '1690'
                });
              }}
              className="inline-block bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              FALAR COM ATENDIMENTO
            </a>
          </div>
        </div>
      </section>

      {/* Opções: Funeral, Sepultamento ou Cremação */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Funeral, Sepultamento ou Cremação
          </h2>
          <p className="text-lg text-gray-700 mb-12 text-center">
            Cada família possui necessidades diferentes.
          </p>
          <p className="text-lg text-gray-700 mb-12 text-center">
            Nossa equipe pode orientar sobre as opções disponíveis para:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Funeral e Sepultamento */}
            <div className="bg-teal-50 rounded-xl p-8 text-center border-2 border-teal-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Funeral e Sepultamento</h3>
              <p className="text-gray-700 leading-relaxed">
                Organização dos serviços funerários necessários até o sepultamento.
              </p>
            </div>

            {/* Cremação */}
            <div className="bg-teal-50 rounded-xl p-8 text-center border-2 border-teal-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Cremação</h3>
              <p className="text-gray-700 leading-relaxed">
                Orientação sobre documentação, procedimentos e realização da cremação.
              </p>
            </div>

            {/* Traslado */}
            <div className="bg-teal-50 rounded-xl p-8 text-center border-2 border-teal-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Traslado Funerário</h3>
              <p className="text-gray-700 leading-relaxed">
                Remoção e transporte funerário conforme a necessidade e o destino.
              </p>
            </div>
          </div>

          <div className="text-center">
            <a
              href="https://wa.me/552141064022?text=Gostaria%20de%20falar%20com%20um%20atendente"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'opcoes_section'
                });
              }}
              className="inline-block bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              FALAR COM UM ATENDENTE
            </a>
          </div>
        </div>
      </section>

      {/* Funerária 24 Horas */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Funerária 24 Horas no Rio de Janeiro
          </h2>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            Em caso de falecimento, nossa equipe está disponível 24 horas para orientar a família sobre os primeiros procedimentos.
          </p>
          <p className="text-lg text-gray-700 mb-8">
            Atendimento para diferentes regiões do Rio de Janeiro, conforme disponibilidade operacional.
          </p>

          <div className="bg-white rounded-xl shadow-lg p-8 mb-8 max-w-2xl mx-auto">
            <p className="text-xl font-bold text-gray-900 mb-4">Atendimento Imediato</p>
            <p className="text-lg text-gray-700 mb-2">
              <strong>Telefone:</strong> (21) 4106-4022
            </p>
            <p className="text-lg text-gray-700 mb-6">
              <strong>WhatsApp:</strong> (21) 4106-4022
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/552141064022?text=Preciso%20de%20atendimento%20funerário%20agora"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: '24horas_section',
                  urgency: 'immediate'
                });
              }}
              className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              CHAMAR NO WHATSAPP
            </a>
            <a
              href="tel:41064022"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'phone_click',
                  click_location: '24horas_section'
                });
              }}
              className="inline-flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <Phone className="w-5 h-5 mr-2" />
              LIGAR AGORA
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Perguntas Frequentes sobre Valores de Funeral
          </h2>
          <div className="space-y-4">
            {faqItems.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-100 transition-colors"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-4">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Precisa Organizar um Funeral Agora?
          </h2>
          <p className="text-xl mb-8 text-teal-50">
            Receba orientação e consulte os valores dos serviços funerários.
          </p>
          <p className="text-2xl font-bold mb-8">ATENDIMENTO 24 HORAS</p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/552141064022?text=Preciso%20consultar%20valor%20de%20funeral"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'cta_final_valor'
                });
              }}
              className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              CONSULTAR VALOR PELO WHATSAPP
            </a>
            <a
              href="tel:41064022"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'phone_click',
                  click_location: 'cta_final_valor'
                });
              }}
              className="inline-flex items-center justify-center bg-white text-teal-700 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <Phone className="w-5 h-5 mr-2" />
              LIGAR PARA A FUNERÁRIA
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FuneralValor;
